import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injector, Injectable } from '@angular/core';
import { defer, EMPTY, Observable, of, timer } from 'rxjs';
import { catchError, filter, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { RefreshResult } from '../../interfaces/user';
import { WebsocketService } from '../websocket/websocket.service';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class AuthRefreshService {
  private refreshTokensRequest$: Observable<RefreshResult> | null = null;
  private initialized = false;

  constructor(private injector: Injector) {}

  public init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;
    this.websocket.authorized$.pipe(
      filter((yes) => yes),
      switchMap(() => this.startTokenRefreshCycle())
    ).subscribe();
  }

  public startTokenRefreshCycle(): Observable<RefreshResult> {
    return defer(() => {
      const tokenLifetime = 10 * 60 * 1000;
      const transientRefreshRetryDelay = 30 * 1000;
      const savedAt = this.authService.tokenSavedAt;
      const now = Date.now();

      if (!savedAt) {
        return of({ error: 'No refresh token available' } as RefreshResult);
      }

      const expiresIn = tokenLifetime - (now - savedAt);
      const dueTime = Math.max(expiresIn, 0);

      return timer(dueTime).pipe(
        switchMap(() => {
          const refreshToken = this.authService.getTokens()?.refreshToken;

          if (!refreshToken) {
            return of({ error: 'No refresh token available' } as RefreshResult);
          }

          return this.refreshTokens(refreshToken);
        }),
        switchMap((result) => {
          if (result.accessToken && result.refreshToken) {
            this.authService.saveTokens(result.accessToken, result.refreshToken);
            console.log('Token refreshed successfully');
          } else if (result.error) {
            if (result.transient) {
              console.warn('Transient token refresh failure:', result.error);
              return timer(transientRefreshRetryDelay).pipe(map(() => result));
            }

            console.error('Failed to refresh token:', result.error);
            this.authService.logout().subscribe();
            return EMPTY;
          }

          return of(result);
        }),
        catchError((err) => {
          console.error('Error during token refresh cycle:', err);
          return EMPTY;
        }),
        switchMap(() => this.startTokenRefreshCycle())
      );
    });
  }

  public refreshTokens(refreshToken: string): Observable<RefreshResult> {
    if (!this.refreshTokensRequest$) {
      this.refreshTokensRequest$ = this.createRefreshTokensRequest(refreshToken).pipe(
        finalize(() => {
          this.refreshTokensRequest$ = null;
        }),
        shareReplay(1)
      );
    }

    return this.refreshTokensRequest$;
  }

  private createRefreshTokensRequest(refreshToken: string): Observable<RefreshResult> {
    if (this.websocket.connected$.getValue()) {
      return this.websocket.refreshTokensViaWS(refreshToken).pipe(
        switchMap((result) => {
          if (result.error) {
            console.warn('WS refresh returned error, fallback to HTTP', result.error);
            return this.refreshTokenViaHttp(refreshToken);
          }
          return of(result);
        }),
        catchError((err) => {
          console.warn('WS refresh failed, fallback to HTTP', err);
          return this.refreshTokenViaHttp(refreshToken);
        })
      );
    }

    return this.refreshTokenViaHttp(refreshToken);
  }

  private refreshTokenViaHttp(refreshToken: string): Observable<RefreshResult> {
    const http = this.injector.get(HttpClient);

    return http.post<{ accessToken: string; refreshToken: string }>('/auth/refresh', { refreshToken }).pipe(
      map((data) => ({
        accessToken: data.accessToken,
        refreshToken: data.refreshToken
      })),
      catchError((error: HttpErrorResponse) => {
        console.error('Token refresh failed', error);
        return of({
          error: error.message,
          status: error.status,
          transient: this.isTransientRefreshError(error),
        });
      })
    );
  }

  private isTransientRefreshError(error: HttpErrorResponse): boolean {
    return error.status === 0 || error.status >= 500;
  }

  private get authService(): AuthService {
    return this.injector.get(AuthService);
  }

  private get websocket(): WebsocketService {
    return this.injector.get(WebsocketService);
  }
}
